import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from '@legendapp/state/react';
import { Button } from '../../../design-system';
import { settings$ } from '../../../store/settings$';
import { ui$, onboardingReplay$, setActiveRightPanelTab } from '../../../store/ui$';
import { openUploadModal } from '../../../store';
import { trackEvent } from '../../../lib/analytics';
import useNewOnboardingPreference from '../../../hooks/useNewOnboardingPreference';
import { OnboardingContext } from './OnboardingContext';
import { advanceProgress, initialProgress, loadProgress, progressKey } from './progress';
import Spotlight from './Spotlight';
import { WelcomeDialog, FeatureIntroductionDialog, JourneyCompletionDialog } from './OnboardingDialogs';
import { JOURNEY_STEPS, INTRO_FEATURES } from './config';
import { getStarterArticle } from './starterArticle';
import './GuidedOnboarding.css';

function Session({ userId, children }) {
  const { t, i18n } = useTranslation('common');
  const location = useLocation();
  const navigate = useNavigate();
  const mounted = useRef(true);
  useLayoutEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const latestPath = useRef(location.pathname);
  useLayoutEffect(() => { latestPath.current = location.pathname; }, [location.pathname]);
  const [progress, setProgress] = useState(() => loadProgress(userId));
  const progressRef = useRef(progress);
  const [intro, setIntro] = useState(null);
  const [completion, setCompletion] = useState(null);
  const [startError, setStartError] = useState(false);
  const [starting, setStarting] = useState(false);
  const startRequest = useRef(0);
  const replay = useSelector(() => onboardingReplay$.get());
  const save = useCallback(next => {
    const state = typeof next === 'function' ? next(progressRef.current) : next;
    if (state === progressRef.current) return;
    localStorage.setItem(progressKey(userId), JSON.stringify(state));
    progressRef.current = state;
    setProgress(state);
  }, [userId]);
  const pause = useCallback(() => { save(p => ({ ...p, paused: true })); trackEvent('onboarding_v2_paused'); }, [save]);
  useEffect(() => {
    if (!replay) return;
    const frame = requestAnimationFrame(() => { save(p => ({ ...initialProgress(), features: p.features, reviewed: p.reviewed })); onboardingReplay$.set(false); setCompletion(null); });
    return () => cancelAnimationFrame(frame);
  }, [replay, save]);
  useLayoutEffect(() => {
    const handler = ({ detail }) => {
      if (detail.userId && detail.userId !== userId) return;
      save(p => {
        if (detail.type === 'card-reviewed' && !p.reviewed && !p.step.startsWith('cards-')) {
          setCompletion('review');
          trackEvent('onboarding_v2_review_completed');
          return { ...p, reviewed: true };
        }
        const next = advanceProgress(p, detail);
        if (next !== p) {
          trackEvent('onboarding_v2_step_completed', { step: p.step });
          if (next.step === 'complete' && p.task === 'cards' && !p.reviewed) trackEvent('onboarding_v2_review_completed');
          if (next.step === 'cards-review') setCompletion('cardSaved');
          if (next.step === 'complete') setCompletion(p.task === 'cards' ? 'review' : p.task === 'ai' ? 'ai' : 'reading');
        }
        return next;
      });
    };
    window.addEventListener('reedle-onboarding-v2', handler);
    return () => window.removeEventListener('reedle-onboarding-v2', handler);
  }, [save, userId]);
  const introduce = useCallback((feature, action) => {
    if (feature !== 'off' && !INTRO_FEATURES.includes(feature)) throw new Error(`Unknown onboarding feature: ${feature}`);
    if (feature === 'off' || progress.features[feature]) return action();
    setIntro({ feature, action, path: location.pathname });
  }, [progress.features, location.pathname]);
  const visibleIntro = intro?.path === location.pathname ? intro : null;
  const confirmFeature = () => {
    const pending = intro;
    setIntro(null);
    save(p => ({ ...p, features: { ...p.features, [pending.feature]: true } }));
    trackEvent('onboarding_v2_feature_confirmed', { feature: pending.feature });
    requestAnimationFrame(() => { if (mounted.current && pending.path === latestPath.current) pending.action(); });
  };
  const addReading = () => {
    setCompletion(null);
    save(p => ({ ...p, paused: true }));
    navigate('/dashboard');
    requestAnimationFrame(openUploadModal);
  };
  const startTask = task => {
    setCompletion(null);
    const isOpen = !ui$.aiPanelHidden.get();
    setActiveRightPanelTab('chat');
    save(p => ({ ...p, task, step: `${task}-${isOpen ? 'ask' : 'open'}`, paused: false }));
    navigate(`/article/${progress.articleId}`);
    trackEvent('onboarding_v2_task_started', { task });
  };
  const reviewSavedCard = () => { setCompletion(null); navigate(`/deck/${progress.deckId}?study=1`); };
  const startReading = async () => {
    if (starting) return;
    const request = ++startRequest.current;
    const path = location.pathname;
    setStarting(true);
    setStartError(false);
    try {
      const article = await getStarterArticle(userId, i18n.resolvedLanguage || i18n.language);
      if (!mounted.current || request !== startRequest.current || latestPath.current !== path || progressRef.current.paused) return;
      save(p => ({ ...p, step: 'open', articleId: article.id, paused: false }));
      navigate(`/article/${article.id}`);
      trackEvent('onboarding_v2_started');
    } catch (error) {
      console.error('[onboarding-v2] Failed to open starter article', { userId, error });
      if (mounted.current && request === startRequest.current) setStartError(true);
    } finally {
      if (mounted.current && request === startRequest.current) setStarting(false);
    }
  };
  const welcome = ['welcome', 'open'].includes(progress.step) && !progress.paused && location.pathname === '/dashboard';
  const active = !welcome && !progress.paused && !visibleIntro && !completion && Boolean(JOURNEY_STEPS[progress.step]) && (
    (progress.step === 'open' && ['/all', '/inbox'].includes(location.pathname))
    || (progress.step === 'cards-review' && location.pathname === `/deck/${progress.deckId}`)
    || (progress.step !== 'open' && progress.step !== 'cards-review' && location.pathname === `/article/${progress.articleId}`)
  );
  return <OnboardingContext.Provider value={{ introduce, enabled: true }}>
    {children}
    {progress.paused && progress.step !== 'complete' && location.pathname === '/dashboard' && <div className="guided-tour__resume"><Button onClick={() => { save(p => ({ ...p, paused: false })); if (progress.step === 'cards-review') navigate(`/deck/${progress.deckId}?study=1`); else if (progress.articleId) navigate(`/article/${progress.articleId}`); }}>{t('guided.resume')}</Button></div>}
    <WelcomeDialog open={welcome} onPause={pause} onStart={startReading} error={startError} loading={starting} />
    {active && <Spotlight step={progress.step} selectors={JOURNEY_STEPS[progress.step].selectors}
      soft={JOURNEY_STEPS[progress.step].soft} onPause={pause} />}
    <FeatureIntroductionDialog intro={visibleIntro} onClose={() => setIntro(null)} onConfirm={confirmFeature} />
    <JourneyCompletionDialog kind={completion} progress={progress} onTask={startTask}
      onImport={addReading} onReview={reviewSavedCard} onClose={() => {
        setCompletion(null);
        if (completion === 'cardSaved') pause();
      }} />
  </OnboardingContext.Provider>;
}

export default function GuidedOnboardingProvider({ userId, children }) {
  const [preview] = useNewOnboardingPreference();
  const developer = useSelector(() => settings$.developerMode.get());
  return preview && developer && userId ? <Session key={userId} userId={userId}>{children}</Session> : children;
}
